import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Clock, CreditCard, Loader2, LogIn, ShieldCheck, UserPlus, XCircle, Hourglass, Receipt, FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/hooks/useCheckout";
import { EnquireButton } from "@/components/EnquireButton";

export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
export const effectivePrice = (c) => (c.plan_type === "free" ? 0 : (c.discount_price != null && c.discount_price < c.price ? c.discount_price : c.price || 0));

function StatusBanner({ status, message, result }) {
  const map = {
    success: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
    pending: { icon: Hourglass, cls: "border-amber-200 bg-amber-50 text-amber-800" },
    failed: { icon: XCircle, cls: "border-rose-200 bg-rose-50 text-rose-800" },
    cancelled: { icon: XCircle, cls: "border-slate-200 bg-slate-50 text-slate-700" },
    processing: { icon: Loader2, cls: "border-blue-200 bg-blue-50 text-blue-800" },
  };
  const m = map[status];
  if (!m) return null;
  const text = status === "cancelled" ? "Checkout closed before payment. You can try again any time." : message;
  return (
    <div className={`mt-5 rounded-xl border p-4 text-sm ${m.cls}`} data-testid={`payment-status-${status}`}>
      <div className="flex items-start gap-2">
        <m.icon className={`mt-0.5 h-4 w-4 flex-none ${status === "processing" ? "animate-spin" : ""}`} />
        <div>
          <div>{text}</div>
          {status === "success" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button asChild size="sm" className="bg-[hsl(var(--brand-navy))]" data-testid="checkout-go-courses"><Link to="/my-courses">Go to My Courses</Link></Button>
              {result?.payment?.id && (
                <Button asChild size="sm" variant="outline" data-testid="checkout-view-receipt"><Link to={`/receipt/${result.payment.id}`}><Receipt className="mr-1 h-3.5 w-3.5" /> View receipt</Link></Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MockDialog({ order, onResult }) {
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onResult(false)}>
      <DialogContent data-testid="mock-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[hsl(var(--brand-orange))]" /> Test checkout (mock)</DialogTitle>
          <DialogDescription>Razorpay keys are not configured, so this payment is simulated. No money is charged.</DialogDescription>
        </DialogHeader>
        {order && <div className="font-display text-2xl font-bold" data-testid="mock-checkout-amount">{inr(order.amount / 100)}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onResult(false)} data-testid="mock-checkout-fail">Simulate failure</Button>
          <Button onClick={() => onResult(true)} className="bg-[hsl(var(--brand-orange))]" data-testid="mock-checkout-pay">Simulate success</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuthGate({ slug }) {
  const next = encodeURIComponent(`/checkout/${slug}`);
  return (
    <div className="mt-6 rounded-xl border border-dashed border-border p-5" data-testid="checkout-auth-gate">
      <div className="font-semibold">Log in or create an account to continue</div>
      <p className="mt-1 text-sm text-muted-foreground">Your purchase will be linked to your PilotYatra student account.</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <Button asChild className="bg-[hsl(var(--brand-navy))]" data-testid="checkout-login-btn"><Link to={`/login?next=${next}`}><LogIn className="mr-2 h-4 w-4" /> Log in</Link></Button>
        <Button asChild variant="outline" data-testid="checkout-register-btn"><Link to={`/register?next=${next}`}><UserPlus className="mr-2 h-4 w-4" /> Create account</Link></Button>
      </div>
    </div>
  );
}

function CourseSummary({ c }) {
  return (
    <Card className="border-border/70">
      <CardContent className="p-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">{c.category || "Course"}</div>
        <h1 className="mt-1 font-display text-2xl font-bold sm:text-3xl" data-testid="checkout-course-name">{c.name}</h1>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="checkout-course-desc">{c.full_description || c.short_description}</p>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
          {c.duration && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.duration}</span>}
          <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {c.access_days ? `${c.access_days} days access` : "Lifetime access"}</span>
        </div>
        {c.subjects?.length > 0 && (
          <div className="mt-6">
            <div className="text-sm font-semibold">Included subjects & chapters</div>
            <div className="mt-3 space-y-2">
              {c.subjects.map((s) => (
                <div key={s.id} className="rounded-lg border border-border/70 p-3 text-sm">
                  <div className="flex justify-between font-medium"><span>{s.name}</span><span className="text-xs text-muted-foreground">{s.question_count} Qs</span></div>
                  {s.chapters?.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{s.chapters.join(" · ")}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Checkout() {
  const { slug } = useParams();
  const { status: authStatus } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const co = useCheckout(course || {});

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setNotFound(true));
  }, [slug]);
  useEffect(() => {
    if (authStatus !== "authenticated" || !course) return;
    api.get("/me/enrollments").then((r) => setEnrolled(r.data.some((e) => e.course_id === course.id && e.status === "active"
      && (!e.expires_at || new Date(e.expires_at) > new Date()))));
  }, [authStatus, course, co.status]);

  if (notFound) return <div className="p-10 text-center">Course not found. <Link to="/courses" className="underline">Browse courses</Link></div>;
  if (!course) return <div className="mx-auto max-w-5xl p-10"><Skeleton className="h-80" /></div>;

  const price = effectivePrice(course);
  const saved = course.discount_price != null && course.discount_price < course.price ? course.price - course.discount_price : 0;
  const closed = course.buy_enabled === false || course.status !== "published";

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="checkout-page">
      <Link to={`/courses/${course.slug}`} className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to course</Link>
      <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
        <CourseSummary c={course} />
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Card className="border-border/70 card-elevated">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 font-display text-lg font-bold"><CreditCard className="h-5 w-5 text-[hsl(var(--brand-orange))]" /> Payment details</div>
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Course fee</span><span>{inr(course.plan_type === "free" ? 0 : course.price)}</span></div>
                {saved > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−{inr(saved)}</span></div>}
                <div className="flex justify-between border-t border-border pt-3 font-display text-xl font-bold"><span>Total payable</span><span data-testid="checkout-total">{price ? inr(price) : "Free"}</span></div>
                <div className="text-xs text-muted-foreground">Inclusive of applicable taxes. Final amount is confirmed by our server.</div>
              </div>
              {enrolled && co.status !== "success" ? (
                <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="checkout-already-enrolled">
                  <CheckCircle2 className="mr-1 inline h-4 w-4" /> You're already enrolled in this course.
                  <Button asChild size="sm" className="mt-3 w-full bg-[hsl(var(--brand-navy))]"><Link to="/my-courses">Go to My Courses</Link></Button>
                </div>
              ) : closed ? (
                <div className="mt-6 rounded-xl border border-border bg-secondary/60 p-4 text-sm" data-testid="checkout-closed">Enrollment for this course is currently closed. Send us an enquiry and we'll help you.</div>
              ) : authStatus !== "authenticated" ? <AuthGate slug={course.slug} /> : co.status !== "success" && (
                <Button className="mt-6 h-12 w-full bg-[hsl(var(--brand-orange))] text-base hover:bg-[hsl(var(--brand-orange))]/90" disabled={co.status === "processing"} onClick={co.pay} data-testid="pay-now-btn">
                  {co.status === "processing" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {price ? `Pay Now · ${inr(price)}` : "Enroll for free"}
                </Button>
              )}
              <StatusBanner status={co.status} message={co.message} result={co.result} />
              <div className="mt-4 flex items-center justify-center gap-1 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Razorpay · UPI, cards, netbanking & wallets</div>
              <EnquireButton course={course.name} className="mt-4 w-full" testId="checkout-enquire-btn" label="Have questions? Enquire Now" />
            </CardContent>
          </Card>
        </aside>
      </div>
      <MockDialog order={co.mockOrder} onResult={co.payMock} />
    </div>
  );
}
